import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import {
  sellersForMarketplace,
  useMarketplaceConnections,
} from "@/api/use-marketplace-connections";

interface SellerSelectProps {
  id: string;
  testId: string;
  marketplace: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Seller dropdown for the promotion form, filtered to the given marketplace.
 *
 * Options come from the marketplace connection list, not from order history
 * (BE spec D5): a shop connected this morning must be choosable before its
 * first order arrives. Shops that are not `active` are rendered but DISABLED —
 * the backend rejects a new rule for them with 409 shop_not_active, and the UI
 * says why rather than hiding the shop and leaving the operator guessing.
 *
 * A stored value is never dropped. A disconnected shop stays selected (with a
 * note, so an existing rule stays readable and editable), and a value that is
 * in no connection at all — a CSV-era id such as `aloshop` — keeps its own
 * flagged option.
 */
export function SellerSelect({
  id,
  testId,
  marketplace,
  value,
  onChange,
  disabled,
  className,
}: SellerSelectProps): JSX.Element {
  const { t } = useTranslation("promotion");
  const { data: connections = [], isLoading } = useMarketplaceConnections();

  const options = sellersForMarketplace(connections, marketplace);
  const selected = options.find((c) => c.shop_id === value);
  const stale = value !== "" && selected === undefined;
  const noChoices = options.length === 0 && !stale;
  const showDisconnectedNote = selected !== undefined && selected.status !== "active";

  return (
    <div>
      <select
        id={id}
        data-testid={testId}
        className={className}
        disabled={disabled || isLoading || noChoices}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{t("seller_select.placeholder")}</option>
        {options.map((c) => (
          <option
            key={c.shop_id}
            value={c.shop_id}
            // The stored value must stay selectable-as-shown even when it is a
            // disconnected shop; disabling it would blank the form's value on
            // some browsers and silently rewrite an existing rule.
            disabled={c.status !== "active" && c.shop_id !== value}
          >
            {t("seller_select.option", { name: c.shop_name || c.shop_id, id: c.shop_id })}
          </option>
        ))}
        {stale && <option value={value}>{t("seller_select.stale", { id: value })}</option>}
      </select>
      {showDisconnectedNote && (
        <p
          className="mt-1 text-xs text-[var(--text-subtle)]"
          data-testid={`${testId}-disconnected-note`}
        >
          {t("seller.disconnected_note")}
        </p>
      )}
      {noChoices && !isLoading && (
        <p className="mt-1 text-xs text-[var(--text-subtle)]" data-testid={`${testId}-empty`}>
          {t("seller_select.empty")}
        </p>
      )}
    </div>
  );
}
